import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { FileText, Search, Eye, Download, Trash2, CheckCircle, Send, XCircle, Clock, Filter, X, Mail } from 'lucide-react';
import SendEmailModal from '../components/shared/SendEmailModal';

const STATUS_OPTIONS = [
  { value: 'all',        label: 'Tüm Durumlar' },
  { value: 'taslak',     label: 'Taslak' },
  { value: 'gönderildi', label: 'Gönderildi' },
  { value: 'imzalandı',  label: 'İmzalandı' },
  { value: 'iptal',      label: 'İptal' },
];

const STATUS_STYLES = {
  'taslak':     { label: 'Taslak',     color: 'bg-gray-100 text-gray-700',  icon: Clock },
  'gönderildi': { label: 'Gönderildi', color: 'bg-blue-100 text-blue-700',  icon: Send },
  'imzalandı':  { label: 'İmzalandı',  color: 'bg-green-100 text-green-700',icon: CheckCircle },
  'iptal':      { label: 'İptal',       color: 'bg-red-100 text-red-700',   icon: XCircle },
};

/* ─── A4 PDF Viewer — fetches PDF from backend (Playwright/Chromium) ─────────── */
function ContractPDFViewer({ contract, onClose }) {
  const [pdfUrl,     setPdfUrl]     = useState(null);
  const [generating, setGenerating] = useState(true);
  const [error,      setError]      = useState(null);
  const urlRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setGenerating(true);
    setError(null);

    const fetchPdf = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/contracts/${contract.id}/pdf`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        });
        if (cancelled) return;
        const url = URL.createObjectURL(res.data);
        urlRef.current = url;
        setPdfUrl(url);
      } catch (err) {
        if (cancelled) return;
        console.error('PDF fetch error:', err);
        setError(err.response?.data?.detail || err.message || 'PDF oluşturulamadı');
      } finally {
        if (!cancelled) setGenerating(false);
      }
    };

    fetchPdf();

    return () => {
      cancelled = true;
      if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    };
  }, [contract]);

  const handleDownload = useCallback(() => {
    if (!pdfUrl) return;
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = `${contract.company_name} - ${contract.template_name}.pdf`;
    a.click();
  }, [pdfUrl, contract]);

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col"
      style={{ background: 'rgba(0,0,0,0.82)' }}
      data-testid="contract-pdf-viewer"
    >
      {/* Top bar */}
      <div className="flex items-center gap-3 bg-gray-900 text-white px-4 py-2.5 shrink-0">
        <FileText className="w-4 h-4 text-teal-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="font-semibold text-sm truncate block">
            {contract.company_name} — {contract.template_name}
          </span>
          <span className="text-xs text-gray-400">
            {contract.exhibition_name} · Stand {contract.stand_number} · A4 PDF
          </span>
        </div>

        {!generating && !error && (
          <Button
            size="sm"
            onClick={handleDownload}
            className="gap-1.5 bg-teal-600 hover:bg-teal-700 text-white"
            data-testid="download-pdf-btn"
          >
            <Download className="w-4 h-4" /> PDF Indir
          </Button>
        )}

        <button
          onClick={onClose}
          className="p-1.5 rounded hover:bg-gray-700 transition-colors ml-1"
          data-testid="close-viewer-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      {generating && (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 text-white">
          <div className="w-10 h-10 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-300">PDF hazırlaniyor, lütfen bekleyin...</p>
          <p className="text-xs text-gray-500">Sunucu tarafında Chromium ile render ediliyor</p>
        </div>
      )}

      {!generating && error && (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 text-white">
          <p className="text-red-400 text-sm">PDF oluşturulamadı: {error}</p>
          <Button variant="outline" size="sm" onClick={onClose}>Kapat</Button>
        </div>
      )}

      {!generating && pdfUrl && (
        <iframe
          src={pdfUrl}
          className="flex-1 w-full"
          style={{ border: 'none', background: '#525659' }}
          title="Sözleşme Önizleme"
          data-testid="pdf-iframe"
        />
      )}
    </div>
  );
}

/* ─── Status Badge ───────────────────────────────────────────────────────────── */
function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES['taslak'];
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`}>
      <Icon className="w-3 h-3" />{s.label}
    </span>
  );
}

/* ─── Main Page ──────────────────────────────────────────────────────────────── */
export default function OrganizerContracts({ embedded = false }) {
  const [contracts,      setContracts]     = useState([]);
  const [loading,        setLoading]       = useState(true);
  const [search,         setSearch]        = useState('');
  const [statusFilter,   setStatusFilter]  = useState('all');
  const [viewContract,   setViewContract]  = useState(null);
  const [deleteTarget,   setDeleteTarget]  = useState(null);
  const [updatingStatus, setUpdatingStatus]= useState(null);
  const [emailTarget,    setEmailTarget]   = useState(null);

  const token = () => localStorage.getItem('token');

  useEffect(() => { fetchContracts(); }, [statusFilter]); // eslint-disable-line

  const fetchContracts = async () => {
    setLoading(true);
    try {
      const q = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const res = await axios.get(`${API}/contracts${q}`, { headers: { Authorization: `Bearer ${token()}` } });
      setContracts(res.data);
    } catch { toast.error('Sözleşmeler yüklenemedi'); }
    finally  { setLoading(false); }
  };

  const handleStatusUpdate = async (id, status) => {
    setUpdatingStatus(id);
    try {
      await axios.put(`${API}/contracts/${id}`, { status }, { headers: { Authorization: `Bearer ${token()}` } });
      toast.success('Durum güncellendi'); fetchContracts();
    } catch { toast.error('Güncelleme başarısız'); }
    finally  { setUpdatingStatus(null); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/contracts/${deleteTarget.id}`, { headers: { Authorization: `Bearer ${token()}` } });
      toast.success('Sözleşme silindi'); setDeleteTarget(null); fetchContracts();
    } catch { toast.error('Silinemedi'); }
  };

  const filtered = contracts.filter(c =>
    [c.company_name, c.exhibition_name, c.stand_number, c.template_name]
      .some(v => v?.toLowerCase().includes(search.toLowerCase()))
  );
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('tr-TR') : '-';

  return (
    <div className={embedded ? "space-y-4" : "p-6 space-y-6"}>
      {!embedded && (
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-7 h-7 text-teal-600" /> Sözleşme Yönetimi
          </h1>
          <p className="text-gray-500 text-sm mt-1">Oluşturulan sözleşmeleri görüntüleyin ve yönetin</p>
        </div>
        <div className="text-sm text-gray-500">Toplam: <strong>{contracts.length}</strong></div>
      </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48 max-w-xs">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <Input placeholder="Firma, fuar veya stand ara..." value={search}
            onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="contract-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="contract-status-filter">
            <Filter className="w-4 h-4 mr-2 text-gray-400" /><SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        {['taslak', 'gönderildi', 'imzalandı', 'iptal'].map(s => {
          const count = contracts.filter(c => c.status === s).length;
          const st = STATUS_STYLES[s]; const Icon = st.icon;
          return (
            <Card key={s}
              className={`cursor-pointer border-2 transition-all ${statusFilter === s ? 'border-teal-400' : 'border-transparent'}`}
              onClick={() => setStatusFilter(statusFilter === s ? 'all' : s)}
              data-testid={`stat-card-${s}`}>
              <CardContent className="py-3 px-4 flex items-center gap-3">
                <span className={`p-2 rounded-lg ${st.color}`}><Icon className="w-4 h-4" /></span>
                <div>
                  <p className="text-lg font-bold leading-none">{count}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{st.label}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-10 text-gray-400">Yükleniyor...</div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center">
          <FileText className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-400 text-sm">
            {search || statusFilter !== 'all' ? 'Arama sonucu bulunamadı' : 'Henüz sözleşme oluşturulmamış'}
          </p>
          <p className="text-xs text-gray-400 mt-1">Stand düzenleme ekranından sözleşme oluşturabilirsiniz.</p>
        </CardContent></Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Firma</TableHead>
                <TableHead>Fuar</TableHead>
                <TableHead>Stand No</TableHead>
                <TableHead>Şablon</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead>Tarih</TableHead>
                <TableHead className="text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(c => (
                <TableRow key={c.id} data-testid={`contract-row-${c.id}`}>
                  <TableCell className="font-medium">{c.company_name || '-'}</TableCell>
                  <TableCell className="text-sm text-gray-600">{c.exhibition_name || '-'}</TableCell>
                  <TableCell className="text-sm">{c.stand_number || '-'}</TableCell>
                  <TableCell className="text-sm text-gray-600">{c.template_name || '-'}</TableCell>
                  <TableCell>
                    <Select value={c.status} onValueChange={v => handleStatusUpdate(c.id, v)} disabled={updatingStatus === c.id}>
                      <SelectTrigger className="h-7 w-36 text-xs border-0 p-0" data-testid={`status-select-${c.id}`}>
                        <StatusBadge status={c.status} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="taslak">Taslak</SelectItem>
                        <SelectItem value="gönderildi">Gönderildi</SelectItem>
                        <SelectItem value="imzalandı">İmzalandı</SelectItem>
                        <SelectItem value="iptal">İptal</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-sm text-gray-500">{fmtDate(c.generated_at)}</TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-teal-600 hover:text-teal-700 hover:bg-teal-50"
                        onClick={() => setEmailTarget(c)}
                        data-testid={`send-email-contract-${c.id}`} title="E-posta Gönder">
                        <Mail className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8"
                        onClick={() => setViewContract(c)}
                        data-testid={`view-contract-${c.id}`} title="PDF Önizleme">
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500 hover:text-red-600"
                        onClick={() => setDeleteTarget(c)}
                        data-testid={`delete-contract-${c.id}`} title="Sil">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {viewContract && (
        <ContractPDFViewer contract={viewContract} onClose={() => setViewContract(null)} />
      )}

      <SendEmailModal
        open={!!emailTarget}
        onClose={() => setEmailTarget(null)}
        companyId={emailTarget?.company_id}
        contractId={emailTarget?.id}
        contractName={emailTarget?.template_name}
        companyName={emailTarget?.company_name}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={o => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sözleşmeyi Sil</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deleteTarget?.company_name}</strong> firması için oluşturulan bu sözleşmeyi silmek istediğinizden emin misiniz?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="confirm-delete-contract"
              className="bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
